import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {CircularProgress, Grid, Typography, useTheme, withStyles} from "@material-ui/core";
import {FormattedMessage, injectIntl, useIntl} from "react-intl";
import { CreditCard, PaymentForm } from 'react-square-web-payments-sdk';
import formatMoney from "../../../utils/formatMoney";
import TextField from "../../../components/TextField";
import Errors from "../../../utils/Errors";
import CountrySelect from "../../../components/CountrySelect";
import RegionInput from "../../../components/RegionInput";
import validateBillingAddress from "../../../utils/validateBillingAddress";

const styles = (theme) => ({
  container: {
    marginTop: theme.spacing(0.5)
  },
  label: {
    marginBottom: 0
  },
});

SquareCardDetailsForm.propTypes = {
  squareMerchantId: PropTypes.string.isRequired,
  squareLocationId: PropTypes.string.isRequired,
  squareAppId: PropTypes.string.isRequired,
  initializingPaymentConfig: PropTypes.bool.isRequired,
  showZip: PropTypes.bool,
  showBillingAddress: PropTypes.bool,
  defaultCountryCode: PropTypes.string,
  amountDue: PropTypes.object.isRequired,
  idempotencyKey: PropTypes.string.isRequired,
  processing: PropTypes.bool.isRequired,
  onPaymentTokenCreated: PropTypes.func.isRequired,
  classes: PropTypes.object,
};

function SquareCardDetailsForm({
                                  squareMerchantId,
                                  squareLocationId,
                                  squareAppId,
                                  amountDue,
                                  idempotencyKey,
                                 defaultCountryCode = 'US',
                                 showBillingAddress = true,
                                 processing,
                                  onPaymentTokenCreated,
                                  classes
                                }) {

  const [initialized, setInitialized] = useState(false);
  const [useBillingAddress, setUseBillingAddress] = useState(showBillingAddress);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState(null);
  const [region, setRegion] = useState(null);
  const [billingAddressErrors, setBillingAddressErrors] = useState(new Errors());

  const [paying, setPaying] = useState(false);
  const [idempotencyPaymentTokenMap, setIdempotencyPaymentTokenMap] = useState(new Map());

  const theme = useTheme();
  const intl = useIntl();

  function handleUpdateFirstname(firstName) {
    setBillingAddressErrors(billingAddressErrors.clear('firstName'));
    setFirstName(firstName);
  }

  function handleUpdateLastname(lastName) {
    setBillingAddressErrors(billingAddressErrors.clear('lastName'));
    setLastName(lastName);
  }

  function handleUpdateStreet1(street1) {
    setBillingAddressErrors(billingAddressErrors.clear('addressLine1'));
    setAddressLine1(street1);
  }

  function handleUpdatePostalCode(postalCode) {
    setBillingAddressErrors(billingAddressErrors.clear('postalCode'));
    setPostalCode(postalCode);
  }

  function handleUpdateCity(city) {
    setBillingAddressErrors(billingAddressErrors.clear('city'));
    setCity(city);
  }

  function handleUpdateRegion(region) {
    setBillingAddressErrors(billingAddressErrors.clear('region'));
    setRegion(region);
  }

  function handleOnPayClick(token, buyer) {
    if(token.status === 'OK' && !processing) {
      const paymentToken = {
        paymentProvider: 'SQUARE',
        squareMerchantId,
        squareLocationId,
        expMonth: token.details.card.expMonth,
        expYear: token.details.card.expYear,
        brand: token.details.card.brand,
        last4: token.details.card.last4,
        nonce: token.token,
        verificationToken: buyer && buyer.token ? buyer.token : null,
        billingAddress: {
          firstName,
          lastName,
          street1: addressLine1,
          street2: addressLine2,
          city,
          postalcode: postalCode,
          region,
          country: countryCode
        }
      };
      const errors = validateBillingAddress(firstName, lastName, addressLine1, city, postalCode, region, countryCode);
      setBillingAddressErrors(errors);
      if(!errors.hasErrors()) {
        onPaymentTokenCreated(paymentToken);
      }
    }
  }

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid item xs={12}>
        <Typography variant={'button'} className={classes.label}>
          <FormattedMessage id={'label.billing-details'}/>
        </Typography>
      </Grid>
      {useBillingAddress &&
       <>
         <Grid item xs={6}>
           <TextField
             label={<FormattedMessage id={"placeholder.first-name"}/>}
             placeholder={intl.formatMessage({id: "placeholder.first-name"})}
             fullWidth={true}
             value={firstName}
             InputLabelProps={{
               shrink: false
             }}
             variant={'outlined'}
             onChange={(e) => handleUpdateFirstname(e.target.value)}
             data-test={'first-name'}
             error={billingAddressErrors.hasError('firstName')}
             helperText={billingAddressErrors.getError('firstName') && <FormattedMessage id={billingAddressErrors.getError('firstName')}/>}
           />
         </Grid>
         <Grid item xs={6}>
           <TextField
             label={<FormattedMessage id={"placeholder.last-name"}/>}
             placeholder={intl.formatMessage({id: "placeholder.last-name"})}
             fullWidth={true}
             value={lastName}
             InputLabelProps={{
               shrink: false
             }}
             variant={'outlined'}
             onChange={(e) => handleUpdateLastname(e.target.value)}
             data-test={'last-name'}
             error={billingAddressErrors.hasError('lastName')}
             helperText={billingAddressErrors.getError('lastName') && <FormattedMessage id={billingAddressErrors.getError('lastName')}/>}
           />
         </Grid>
         <Grid item xs={12}>
           <TextField
             label={<FormattedMessage id={"placeholder.billing-address-street1"}/>}
             placeholder={intl.formatMessage({id: "placeholder.billing-address-street1"})}
             fullWidth={true}
             value={addressLine1}
             InputLabelProps={{
               shrink: false
             }}
             variant={'outlined'}
             onChange={(e) => handleUpdateStreet1(e.target.value)}
             data-test={'address-line1'}
             error={billingAddressErrors.hasError('addressLine1')}
             helperText={billingAddressErrors.getError('addressLine1') && <FormattedMessage id={billingAddressErrors.getError('addressLine1')}/>}
           />
         </Grid>
         <Grid item xs={12}>
           <TextField
             label={<FormattedMessage id={"placeholder.billing-address-street2"}/>}
             placeholder={intl.formatMessage({id: "placeholder.billing-address-street2"})}
             fullWidth={true}
             value={addressLine2}
             InputLabelProps={{
               shrink: false
             }}
             variant={'outlined'}
             onChange={(e) => setAddressLine2(e.target.value)}
             data-test={'address-line2'}
             error={billingAddressErrors.hasError('addressLine2')}
             helperText={billingAddressErrors.getError('addressLine2') && <FormattedMessage id={billingAddressErrors.getError('addressLine2')}/>}
           />
         </Grid>
         <Grid item xs={8}>
           <TextField
             label={<FormattedMessage id={"placeholder.billing-address-city"}/>}
             placeholder={intl.formatMessage({id: "placeholder.billing-address-city"})}
             fullWidth={true}
             value={city}
             InputLabelProps={{
               shrink: false
             }}
             variant={'outlined'}
             onChange={(e) => handleUpdateCity(e.target.value)}
             data-test={'city'}
             error={billingAddressErrors.hasError('city')}
             helperText={billingAddressErrors.getError('city') && <FormattedMessage id={billingAddressErrors.getError('city')}/>}
           />
         </Grid>
         <Grid item xs={4}>
           <TextField
             label={<FormattedMessage id={"placeholder.billing-address-postalcode"}/>}
             placeholder={intl.formatMessage({id: "placeholder.billing-address-postalcode"})}
             fullWidth={true}
             value={postalCode}
             InputLabelProps={{
               shrink: false
             }}
             variant={'outlined'}
             onChange={(e) => handleUpdatePostalCode(e.target.value)}
             data-test={'postalcode'}
             error={billingAddressErrors.hasError('postalcode')}
             helperText={billingAddressErrors.getError('postalcode') && <FormattedMessage id={billingAddressErrors.getError('postalcode')}/>}
           />
         </Grid>
         <Grid item xs={6}>
           <CountrySelect
             label={<FormattedMessage id={"placeholder.billing-address-country"}/>}
             fullWidth={true}
             value={countryCode}
             variant={'outlined'}
             onChange={(e) => setCountryCode(e.target.value)}
             data-test={'countrycode'}
             error={billingAddressErrors.hasError('countrycode')}
             helperText={billingAddressErrors.getError('countrycode') && <FormattedMessage id={billingAddressErrors.getError('countrycode')}/>}
           />
         </Grid>
         <Grid item xs={6}>
           <RegionInput
             id={'billing-address-region'}
             countryCode={countryCode}
             value={region}
             fullWidth
             onChange={(e) => handleUpdateRegion(e.target.value)}
             error={billingAddressErrors.getError('region')}
             helperText={billingAddressErrors.getError('region') && <FormattedMessage id={billingAddressErrors.getError('region')}/>}
           />
         </Grid>
       </>
      }
      <Grid item xs={12}>
        <PaymentForm
          /**
           * Identifies the calling form with a verified application ID generated from
           * the Square Application Dashboard.
           */
          applicationId={squareAppId}
          /**
           * Invoked when payment form receives the result of a tokenize generation
           * request. The result will be a valid credit card or wallet token, or an error.
           */
          cardTokenizeResponseReceived={(token, buyer) => {
            handleOnPayClick(token, buyer);
          }}
          /**
           * This function enable the Strong Customer Authentication (SCA) flow
           *
           * We strongly recommend use this function to verify the buyer and reduce
           * the chance of fraudulent transactions.
           */
          createVerificationDetails={() => ({
            amount: Intl.NumberFormat('en', {minimumFractionDigits: 2}).format(amountDue.amount / Math.pow(10, 2)),
            /* collected from the buyer */
            billingContact: {
              addressLines: [addressLine1, addressLine2],
              familyName: lastName,
              givenName: firstName,
              country: countryCode,
              region: region,
              city: city,
            },
            currencyCode: amountDue.currencyCode,
            intent: 'CHARGE',
          })}
          /**
           * Identifies the location of the merchant that is taking the payment.
           * Obtained from the Square Application Dashboard - Locations tab.
           */
          locationId={squareLocationId}
        >
          <CreditCard
            postalCode={postalCode}
            buttonProps={{
              css: {
                "[data-theme='dark'] &": {
                  backgroundColor: theme.palette.primary.main,
                  color: 'var(--ifm-color-emphasis-100)',
                  '&:hover': {
                    // backgroundColor: '#0091ea',
                  },
                },
                backgroundColor: theme.palette.primary.main,
                fontSize: '14px',
                fontFamily: theme.typography.button.fontFamily,
                fontWeight: theme.typography.button.fontWeight,
                letterSpacing: theme.typography.button.letterSpacing,
                textTransform: theme.typography.button.textTransform,
                lineHeight: theme.typography.button.lineHeight,
                color: '#fff',
                '&:hover': {
                  // backgroundColor: '#530f16',
                },
              },
            }}
            style={{
              input: {
                fontSize: '14px',
              },
              'input::placeholder': {
                color: '#771520',
              },
            }}
          >
            {(paying || processing) && <FormattedMessage id={"button.processing"}/>}
            {(!paying && !processing) && <FormattedMessage id={"button.pay"}
                                          values={{amount: formatMoney(amountDue.amount, amountDue.currencyCode)}}/>}
          </CreditCard>
        </PaymentForm>
      </Grid>
    </Grid>
  );
}

export default injectIntl(withStyles(styles)(SquareCardDetailsForm));
